import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth-store";

// NEXT_PUBLIC_API_URL puede venir con o sin el sufijo `/api/v1`. Cualquiera de
// estos formatos funciona:
//   `https://shazon.exedra.space/api/v1`  (con sufijo, lo respetamos)
//   `https://shazon.exedra.space`         (sin sufijo, lo añadimos)
//   `/api/v1`                              (relativo detrás de nginx)
//   ""  (vacío) → `/api/v1` relativo por defecto
// Antes este código añadía SIEMPRE `/api/v1` al BASE_URL, lo que duplicaba
// el path cuando el env ya lo incluía.
const BASE_URL = process.env.NEXT_PUBLIC_API_URL || "";
// Exportada para construir URLs de navegación directa (p.ej. el arranque del
// flujo SSO, que es un window.location.href al backend, no una petición axios).
export const API_URL = BASE_URL
  ? (BASE_URL.replace(/\/$/, "").endsWith("/api/v1")
      ? BASE_URL.replace(/\/$/, "")
      : `${BASE_URL.replace(/\/$/, "")}/api/v1`)
  : "/api/v1";


export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh state management
let isRefreshing = false;
// Cada request que llega mientras hay un refresh en curso se suscribe con dos
// callbacks: `onSuccess` (se llama con el nuevo token) y `onFail` (se llama
// con el error del refresh, para poder REJECT su promesa). Antes solo
// guardábamos `onSuccess` y al fallar el refresh las promesas se quedaban
// colgadas indefinidamente → spinner infinito.
type PendingSubscriber = {
  onSuccess: (token: string) => void;
  onFail: (error: unknown) => void;
};
let refreshSubscribers: PendingSubscriber[] = [];

const subscribeTokenRefresh = (subscriber: PendingSubscriber) => {
  refreshSubscribers.push(subscriber);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(({ onSuccess }) => onSuccess(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach(({ onFail }) => onFail(error));
  refreshSubscribers = [];
};

// Endpoints de auth que NUNCA deben pasar por el flujo de refresh cuando dan
// 401. Si /auth/sso/session cae aquí, un usuario sin cookie válida de Exedra
// pero con tokens viejos en localStorage entraba en bucle: 401 → refresh
// → 401 → logout → /login → SSO silencioso → 401 → ...
const AUTH_ENDPOINTS_NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/sso/session"];

// Request interceptor to add auth token + X-Locale
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Locale desde la cookie NEXT_LOCALE (dinámico, sin acoplar al provider
    // de next-intl para poder usarse fuera de un hook). El backend le da
    // prioridad sobre user.locale, así que un cambio en el switcher aplica
    // inmediatamente sin esperar al PUT /profile/locale.
    if (typeof document !== "undefined" && config.headers) {
      const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
      if (m) {
        try {
          config.headers["X-Locale"] = decodeURIComponent(m[1]);
        } catch {
          // Cookie corrupta: ignorar, el backend cae a Accept-Language.
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh with concurrent request handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401 for token expiration, not for other auth errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Endpoints de auth no pueden pasar por refresh: darían bucles
      // (login, refresh, y SSO silencioso son los que crean/renuevan el token,
      // no consumidores).
      const url = originalRequest.url || "";
      if (AUTH_ENDPOINTS_NO_REFRESH.some((e) => url.includes(e))) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If a refresh is already in progress, wait for it to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh({
            onSuccess: (newToken) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              resolve(apiClient(originalRequest));
            },
            onFail: (refreshErr) => reject(refreshErr),
          });
        });
      }

      const refreshToken = useAuthStore.getState().refreshToken;

      // Only try to refresh if we have a refresh token
      if (refreshToken) {
        isRefreshing = true;

        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token } = response.data;

          useAuthStore.getState().setTokens(access_token, refresh_token);

          // Notify all waiting requests
          onTokenRefreshed(access_token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }

          isRefreshing = false;

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          // Notifica a las suscritas: rechaza sus promesas (antes las
          // dejaba colgadas para siempre → spinners infinitos).
          onRefreshFailed(refreshError);

          // Refresh token caducado/invalido → sesion finalizada. Es flujo esperado al
          // arrancar con un token viejo en localStorage; no spameamos console.error.
          const refreshStatus = (refreshError as AxiosError)?.response?.status;
          if (refreshStatus !== 401) {
            console.warn('Token refresh failed:', refreshError);
          }
          useAuthStore.getState().logout();
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available - user needs to login
        useAuthStore.getState().logout();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }

    // For 403 or other errors, just reject without logging out
    return Promise.reject(error);
  }
);

// Helper to convert snake_case to camelCase
export const toCamelCase = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
        letter.toUpperCase()
      );
      (result as Record<string, unknown>)[camelKey] = toCamelCase(
        (obj as Record<string, unknown>)[key]
      );
      return result;
    }, {} as Record<string, unknown>);
  }
  return obj;
};

// Helper to convert camelCase to snake_case
export const toSnakeCase = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCase);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      (result as Record<string, unknown>)[snakeKey] = toSnakeCase(
        (obj as Record<string, unknown>)[key]
      );
      return result;
    }, {} as Record<string, unknown>);
  }
  return obj;
};
